import type { ReactNode } from "react";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/**
 * One group of related fields: a label column on the left, the controls on the right. The controls
 * are capped at `max-w-3xl` so a wide screen never stretches a port or a key across the page.
 */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-4 p-5 xl:grid-cols-[220px_minmax(0,1fr)] xl:gap-8">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="max-w-3xl space-y-4">{children}</div>
    </section>
  );
}

/** A labelled control with either its validation error or, when valid, an optional hint. */
export function SettingsField({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p className="text-sm text-destructive-ink">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** A boolean setting: the switch sits with its label rather than at the far edge of the row. */
export function SettingsSwitch({
  id,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border bg-muted/20 px-4 py-3">
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="mt-0.5"
      />
      <div className="space-y-0.5">
        <Label htmlFor={id} className="cursor-pointer">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

/**
 * The form's action bar. Save is enabled only with something to save, and Discard returns the
 * fields to the stored values — so the operator can always tell whether what is on screen is live.
 */
export function SettingsFormFooter({
  isDirty,
  isSaving,
  onDiscard,
}: {
  isDirty: boolean;
  isSaving: boolean;
  onDiscard: () => void;
}) {
  return (
    <div className="flex flex-col-reverse gap-3 border-t bg-muted/30 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {isDirty ? "You have unsaved changes." : "All changes saved."}
      </p>
      <div className="flex gap-2">
        {isDirty && (
          <Button
            type="button"
            variant="ghost"
            disabled={isSaving}
            onClick={onDiscard}
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Discard
          </Button>
        )}
        <Button type="submit" disabled={isSaving || !isDirty}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" /> Save changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Shown in place of the form when the stored settings could not be read. Without it the form
 * rendered its defaults under "All changes saved" — values that were never stored anywhere.
 */
export function SettingsLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-5 py-12 text-center">
      <p className="text-sm text-muted-foreground">
        Could not load the stored settings.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/** Placeholder rows while the stored settings load. */
export function SettingsFormSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-4 p-5">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-10 animate-pulse rounded bg-muted/60" />
      ))}
    </div>
  );
}
